import { Expose, Type } from 'class-transformer';

import { SnAlarm } from '@lib/api/entities';
import { SnCad } from '@lib/cad';
import { SnModel } from '@lib/common/models';

import {
  AxonAlertSounds,
  AxonAlertActionType,
  AxonAlertStyleColors,
  AxonAlertStyleIcons,
} from '../services/alerts.constants';

export class AxonAlertOwnerMetadata extends SnModel {
  @Expose()
  public callSign: string;

  @Expose()
  public rank: string;
}

export class AxonAlertOwner extends SnModel {
  @Expose()
  public id: string;

  @Expose()
  public firstName: string;

  @Expose()
  public lastName: string;

  @Expose()
  @Type(() => AxonAlertOwnerMetadata)
  public metadata: AxonAlertOwnerMetadata;
}

export class AxonAlertDevice extends SnModel {
  @Expose()
  public id: string;

  @Expose()
  public brand: string;

  @Expose()
  public model: string;

  @Expose()
  public category: string;
}

export class AxonAlertEnrichments extends SnModel {
  @Expose()
  @Type(() => AxonAlertOwner)
  public owner: AxonAlertOwner;

  @Expose()
  @Type(() => AxonAlertDevice)
  public device: AxonAlertDevice;
}

export class AxonAlertDisplayStrings extends SnModel {
  @Expose()
  public title: string;

  @Expose()
  public body: string;
}

export class AxonAlertEventContext extends SnModel {
  @Expose()
  public integration: string;

  @Expose()
  public eventSource: string;

  @Expose()
  public triggerName: string;
}

export class AxonAlertCondition extends SnModel {
  @Expose()
  public type: string;

  @Expose()
  public operator: string;

  @Expose()
  public value: number;
}

export class AxonAlertAction extends SnModel {
  @Expose()
  public type: AxonAlertActionType;

  @Expose()
  public target: object;

  @Expose()
  public order: number;
}

export class AxonAlertPointOfInterestGeometry extends SnModel {
  @Expose()
  public type: 'Point';

  @Expose()
  public coordinates: [number, number];
}

export class AxonAlertPointOfInterestProperties extends SnModel {
  @Expose()
  public address: string;

  @Expose()
  public w3w: string;

  @Expose()
  public floor: string;
}

export class AxonAlertPointOfInterest extends SnModel {
  @Expose()
  public type: 'Feature';

  @Expose()
  @Type(() => AxonAlertPointOfInterestGeometry)
  public geometry: AxonAlertPointOfInterestGeometry;

  @Expose()
  @Type(() => AxonAlertPointOfInterestProperties)
  public properties: AxonAlertPointOfInterestProperties;
}

export class AxonAlertStyles extends SnModel {
  @Expose()
  public icon: AxonAlertStyleIcons;

  @Expose()
  public color: AxonAlertStyleColors;

  @Expose()
  public sound: AxonAlertSounds;

  constructor(data: any = {}) {
    super();
    this.fromJSON(data);
  }
}

export class AxonAlertMetadata extends SnModel {
  @Expose()
  public severity: 'low' | 'medium' | 'high' | 'critical';
}

export class AxonAlertClientFilters extends SnModel {
  @Expose()
  public groupingId: string;

  @Expose()
  public replacementId: string;
}

export class AxonAlertModel extends SnModel {
  @Expose()
  public id: string;

  @Expose()
  public momentIds: string[];

  @Expose()
  public type: string;

  @Expose()
  public hasPII: boolean;

  @Expose()
  @Type(() => AxonAlertDisplayStrings)
  public displayStrings: AxonAlertDisplayStrings;

  @Expose()
  @Type(() => AxonAlertEnrichments)
  public enrichments: AxonAlertEnrichments;

  @Expose()
  public channelType: string;

  @Expose()
  public ruleId: string;

  @Expose()
  public momentCreated: string;

  @Expose()
  public alertCreated: string;

  @Expose()
  public alertUpdated: string;

  @Expose()
  @Type(() => AxonAlertEventContext)
  public eventContext: AxonAlertEventContext;

  @Expose()
  public status: string;

  @Expose()
  public alertState: Record<string, any>;

  @Expose()
  @Type(() => AxonAlertCondition)
  public conditions: AxonAlertCondition[];

  @Expose()
  @Type(() => AxonAlertAction)
  public actions: AxonAlertAction[];

  @Expose()
  @Type(() => AxonAlertPointOfInterest)
  public pointsOfInterest: AxonAlertPointOfInterest[];

  @Expose()
  @Type(() => AxonAlertStyles)
  public styles: AxonAlertStyles;

  @Expose()
  @Type(() => AxonAlertMetadata)
  public metadata: AxonAlertMetadata;

  @Expose()
  public cxid: string;

  @Expose()
  @Type(() => AxonAlertClientFilters)
  public clientFilters: AxonAlertClientFilters;

  constructor(data: any = {}) {
    super();
    this.fromJSON(data);
  }

  /**
   * Helper method to get the full owner name
   */
  get ownerFullName(): string {
    if (!this.enrichments?.owner) {
      return '';
    }
    return `${this.enrichments.owner.firstName} ${this.enrichments.owner.lastName}`.trim();
  }

  /**
   * Helper method to get formatted moment created date
   */
  get momentCreatedDate(): Date | null {
    if (!this.momentCreated) {
      return null;
    }
    return new Date(this.momentCreated);
  }

  /**
   * Helper method to get formatted alert created date
   */
  get alertCreatedDate(): Date | null {
    if (!this.alertCreated) {
      return null;
    }
    return new Date(this.alertCreated);
  }

  /**
   * Helper method to get formatted alert updated date
   */
  get alertUpdatedDate(): Date | null {
    if (!this.alertUpdated) {
      return null;
    }
    return new Date(this.alertUpdated);
  }

  /**
   * Static factory method to create AxonAlert from AlertData interface
   */
  static fromAlertData(alertData: any): AxonAlertModel {
    return new AxonAlertModel(alertData);
  }

  static fromAlarm(alarmData: SnAlarm): AxonAlertModel {
    const axonAlert = new AxonAlertModel({
      id: alarmData.id,
      displayStrings: {
        title: alarmData.type,
        body: 'Test Fusus Alert',
      },
      styles: {
        icon: 'warning_icon',
        color: 'critical_red',
        sound: 'alert',
      },
      actions: [
        {
          type: 'viewDetails',
          target: {},
          order: 1,
        },
        {
          type: 'dockNearby',
          target: {},
          order: 2,
        },
        {
          type: 'snooze',
          target: {},
          order: 3,
        },
      ],
      momentCreated: alarmData.createdAt,
    });

    return axonAlert;
  }

  static fromCad(cadData: SnCad): AxonAlertModel {
    const title = `[P${cadData.properties.priority}] ${cadData.properties.callType}`;

    const body = cadData.properties.id + ' - ' + cadData.properties.address;

    const axonAlert = new AxonAlertModel({
      id: cadData.id.toString(),
      displayStrings: {
        title,
        body,
      },
      styles: {
        icon: 'cad_diamond',
        color: 'critical_red',
        sound: 'alert',
      },
      actions: [
        {
          type: 'viewDetails',
          target: {},
          order: 1,
        },
      ],
      momentCreated: cadData.properties.receivedTime,
    });

    return axonAlert;
  }
}
